import { Input, Switch, Table, Tag, Tooltip } from "antd";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import SectionWrapper from "../../components/SectionWrapper";
import ShowTotal from "../../components/ShowTotal";
import apiPath from "../../constants/apiPath";
import { AppStateContext } from "../../context/AppContext";
import { Severty, ShowToast } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import useRequest from "../../hooks/useRequest";

const Search = Input.Search;

function Index() {
  const { setPageHeading } = useContext(AppStateContext);
  const { request } = useRequest();
  const sectionName = "Newsletter";

  const [searchText, setSearchText] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [tableFilters, setTableFilters] = useState({});
  const [sorterState, setSorterState] = useState({});
  const [refresh, setRefresh] = useState(false);
  const debouncedSearchText = useDebounce(searchText, 300);

  const handleChangeStatus = (id) => {
    request({
      url: `${apiPath.newsletterStatus}/${id}`,
      method: "GET",
      onSuccess: (data) => {
        setRefresh((prev) => !prev);
        ShowToast(data.message, Severty.SUCCESS);
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message || "Unable to update subscriber status", Severty.ERROR);
      },
    });
  };

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
      render: (_, { email }) => (email ? <span>{email}</span> : "-"),
    },
    {
      title: "Submit Count",
      dataIndex: "submitCount",
      key: "submitCount",
      sorter: true,
      render: (_, { submitCount }) => Number(submitCount || 0).toLocaleString("en-IN"),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      filters: [
        { text: "Active", value: "true" },
        { text: "Inactive", value: "false" },
      ],
      render: (_, { isActive }) => <Tag color={isActive ? "green" : "red"}>{isActive ? "Active" : "Inactive"}</Tag>,
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (_, { createdAt }) => (createdAt ? moment(createdAt).format("DD-MMM-YYYY hh:mm A") : "-"),
    },
    {
      title: "Action",
      fixed: "right",
      key: "action",
      render: (_, record) => (
        <Tooltip title={record?.isActive ? "Deactivate" : "Activate"} color="purple">
          <Switch checked={record?.isActive} onChange={() => handleChangeStatus(record?._id)} />
        </Tooltip>
      ),
    },
  ];

  const fetchData = (page = pagination, filters = tableFilters, sorter = sorterState) => {
    const statusFilter = filters?.isActive?.length ? filters.isActive.join(",") : "";
    const sortField = sorter?.field || "";
    const sortOrder = sorter?.order || "";

    request({
      url: `${apiPath.newsletter}?page=${page?.current || 1}&pageSize=${page?.pageSize || 10}&search=${debouncedSearchText}&status=${statusFilter}&sortField=${sortField}&sortOrder=${sortOrder}`,
      method: "GET",
      onSuccess: ({ data }) => {
        const docs = data?.docs || [];
        setList(
          docs.map((item) => ({
            ...item,
            key: item._id,
          })),
        );
        setPagination((prev) => ({
          ...prev,
          current: page?.current || 1,
          pageSize: page?.pageSize || 10,
          total: data?.totalDocs || 0,
        }));
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error?.response?.data?.message || "Unable to fetch newsletter subscribers", Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    setPageHeading(`${sectionName} Management`);
  }, [setPageHeading]);

  useEffect(() => {
    setLoading(true);
    fetchData({ ...pagination, current: 1 }, tableFilters, sorterState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText, refresh]);

  const handleTableChange = (nextPagination, filters, sorter) => {
    setTableFilters(filters || {});
    setSorterState(sorter || {});
    setLoading(true);
    fetchData(nextPagination, filters || {}, sorter || {});
  };

  return (
    <SectionWrapper
      cardHeading={`All ${sectionName} Subscribers`}
      extra={
        <div className="w-100 text-head_right_cont">
          <Search className="searchInput" placeholder="Search by email" onChange={(event) => setSearchText(event.target.value)} allowClear />
        </div>
      }
    >
      <h4 className="text-right mb-1cont-space cont-space">{ShowTotal(pagination.total || 0)}</h4>
      <div className="table-responsive customPagination checkBoxSrNo">
        <Table
          loading={loading}
          columns={columns}
          dataSource={list}
          pagination={{
            defaultPageSize: 10,
            responsive: true,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ["10", "20", "30", "50"],
          }}
          onChange={handleTableChange}
          className="ant-border-space"
        />
      </div>
    </SectionWrapper>
  );
}

export default Index;
